// ADDED: the page the emailed reset link opens (/reset-password?token=...).
// It trades the one-time token for a new password, then sends the user to log in.
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import axios, { getErrorMessage } from '../../api/axios';

const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');

        if (!PASSWORD_REGEX.test(newPassword)) {
            setErrorMessage('Password must have 8+ characters, an uppercase letter, a number and a special character.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setErrorMessage('Passwords do not match.');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await axios.post('/reset-password', { token, newPassword });
            setSuccessMessage(response.data.message);
            // Give the user a moment to read the confirmation before moving on
            setTimeout(() => navigate('/login'), 2500);
        } catch (error) {
            setErrorMessage(getErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    // A link without a token is not usable
    // MODIFIED (styling): both states now use the shared card/button classes, so this
    // screen follows the theme instead of being a hardcoded red page.
    if (!token) {
        return (
            <section className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--surface)' }}>
                <div className="w-full max-w-md cv-card px-6 py-8 text-center space-y-4 animate-fade-up">
                    <h1 className="cv-display text-2xl">Invalid reset link</h1>
                    <p className="text-sm cv-muted">This link is missing its token. Please request a new one.</p>
                    <button onClick={() => navigate('/login')} className="cv-btn cv-btn-primary w-full py-3">
                        Back to login
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
                 style={{ backgroundColor: 'var(--surface)' }}>

            {/* Same red glow as the login and landing screens */}
            <div aria-hidden="true"
                 className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[26rem] w-[26rem] rounded-full blur-3xl opacity-25"
                 style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)' }} />

            <div className="relative w-full max-w-md animate-fade-up">
                <p className="text-center mb-6 font-display font-bold text-3xl tracking-tight">
                    <span className="cv-strong">SPEED</span><span className="cv-accent italic">VAULT</span>{/* MODIFIED: renamed from CLOUD VAULT */}
                </p>

                <div className="cv-card overflow-hidden">
                    <div className="cv-stripe" />
                    <div className="px-6 py-8 sm:px-9 sm:py-9 space-y-5">
                        <div className="text-center space-y-1">
                            <p className="cv-eyebrow">Password reset</p>
                            <h1 className="cv-display text-2xl sm:text-3xl">Choose a new password</h1>
                        </div>

                        {errorMessage && <p role="alert" className="cv-alert-error text-center">{errorMessage}</p>}
                        {successMessage && <p className="cv-alert-success text-center">{successMessage}</p>}

                        {!successMessage &&
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="flex flex-row space-x-3 items-center">
                                    <img src="img/lock-white.png" alt="" className="h-5 w-5 shrink-0 opacity-70 dark:invert" />
                                    <input type="password" placeholder="New password" required autoComplete="new-password"
                                        value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                                        className="cv-input-line" />
                                </div>
                                <div className="flex flex-row space-x-3 items-center">
                                    <img src="img/lock-black.png" alt="" className="h-5 w-5 shrink-0 opacity-70 dark:invert" />
                                    <input type="password" placeholder="Confirm new password" required autoComplete="new-password"
                                        value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="cv-input-line" />
                                </div>
                                <button type="submit" disabled={isSubmitting} className="cv-btn cv-btn-primary w-full py-3 uppercase">
                                    {isSubmitting ? 'Saving...' : 'Reset password'}
                                </button>
                            </form>
                        }
                    </div>
                </div>
            </div>
        </section>
    );
}
